import { registerWidget, type CellContentProps } from '../registry'

/**
 * CompositeSummaryContent — one-line summary renderer for composite kinds
 * that have no dedicated full widget (address, personal-name). Assembles
 * a human-readable string from the sub-field dict. Pure metadata from the
 * cell value — no sub_structure wire metadata needed at render time.
 */

function formatAddress(v: Record<string, unknown>): string {
  if (v['line_1']) {
    const legacy = [v['line_1'], v['city'], v['postcode']].filter(Boolean).map(String)
    if (v['country_code']) legacy.push(String(v['country_code']).toUpperCase())
    return legacy.join(', ')
  }
  const parts: string[] = []
  const street = [v['street'], v['number']].filter(Boolean).join(' ')
  if (street) parts.push(street)
  const postalCode = v['postal_code'] ?? v['postcode']
  if (postalCode) parts.push(String(postalCode))
  if (v['city']) parts.push(String(v['city']))
  const country = v['country'] ?? v['country_code']
  if (country) parts.push(String(country).toUpperCase())
  return parts.join(', ')
}

function formatPersonalName(v: Record<string, unknown>): string {
  return [v['first'], v['middle'], v['last']].filter(Boolean).join(' ')
}

function makeCompositeReader(format: (v: Record<string, unknown>) => string) {
  return function CompositeSummaryContent({ value }: CellContentProps) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return <span className="text-muted-foreground">—</span>
    }
    const text = format(value as Record<string, unknown>)
    if (!text) return <span className="text-muted-foreground">—</span>
    return <span className="truncate">{text}</span>
  }
}

registerWidget('address',       { read: makeCompositeReader(formatAddress),      defaultWidth: 240 })
registerWidget('personal-name', { read: makeCompositeReader(formatPersonalName), defaultWidth: 180 })
